import type { ChatMessage } from './types'

const LAST_READ_KEY = 'kit.chat.lastReadId'
const GROUP_GAP_MS = 5 * 60 * 1000

export function readLastReadId(): number {
  try {
    const v = localStorage.getItem(LAST_READ_KEY)
    const n = v ? parseInt(v, 10) : 0
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

export function saveLastReadId(id: number) {
  try {
    localStorage.setItem(LAST_READ_KEY, String(id))
  } catch {
    /* ignore */
  }
}

export function formatChatTime(iso?: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export function formatDateSeparator(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Сегодня'
  if (d.toDateString() === yesterday.toDateString()) return 'Вчера'
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function truncateText(text: string, max = 60) {
  const t = text.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1)}…`
}

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi

export function linkifyParts(text: string): { type: 'text' | 'link'; value: string }[] {
  if (!text) return []
  const parts: { type: 'text' | 'link'; value: string }[] = []
  let last = 0
  let match: RegExpExecArray | null
  const re = new RegExp(URL_REGEX)
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) })
    }
    parts.push({ type: 'link', value: match[1] })
    last = match.index + match[1].length
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) })
  return parts.length ? parts : [{ type: 'text', value: text }]
}

export function initials(name?: string) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export function sortMessagesAsc(list: ChatMessage[]) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.created_at).getTime()
    const tb = new Date(b.created_at).getTime()
    if (ta !== tb) return ta - tb
    return a.id - b.id
  })
}

export function mergeMessages(existing: ChatMessage[], incoming: ChatMessage[]) {
  const map = new Map<number, ChatMessage>()
  existing.forEach((m) => map.set(m.id, m))
  incoming.forEach((m) => map.set(m.id, m))
  return sortMessagesAsc([...map.values()])
}

export function isSameGroup(a?: ChatMessage | null, b?: ChatMessage | null) {
  if (!a || !b) return false
  if (a.user_id !== b.user_id) return false
  if (a.deleted_at || b.deleted_at) return false
  const ta = new Date(a.created_at).getTime()
  const tb = new Date(b.created_at).getTime()
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return false
  return Math.abs(tb - ta) <= GROUP_GAP_MS
}

/** Vite proxy: absolute :3123 URLs break cookie auth on <img>. Keep /api/... relative. */
export function resolveAttachmentUrl(url: string) {
  if (!url) return ''
  try {
    if (url.startsWith('/')) return url
    const u = new URL(url, window.location.origin)
    if (u.pathname.startsWith('/api/')) {
      return `${u.pathname}${u.search}`
    }
    return url
  } catch {
    return url
  }
}

export const QUICK_EMOJIS = ['👍', '🙂', '😂', '🔥', '✅', '❗', '🙏', '🎉', '👀', '❤️']
